import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { captureException } from '@sentry/react';
import { getShareUsersV1Auth0ThoughtspotobjectsObjectIdShareusers } from '@/api/generated/endpoints/dashboard';
import type { ShareGetUsersResponseSchema } from '@/api/generated/schemas';
import { queryKeys } from '@/api/queryKeys';
import {
  BASE_RETRY_DELAY_MS,
  CRITICAL_QUERY_RETRY_COUNT,
  DEFAULT_QUERY_STALE_TIME,
  MAX_RETRY_DELAY_MS,
} from '@/constants/queries';

interface UseShareUsersQueryParams {
  objectId: string | null;
  options?: Partial<UseQueryOptions<ShareGetUsersResponseSchema, Error>>;
}

export function useShareUsersQuery({ objectId, options }: UseShareUsersQueryParams) {
  return useQuery({
    queryKey: queryKeys.shareUsers.byObjectId(objectId ?? ''),
    queryFn: async () => {
      try {
        return await getShareUsersV1Auth0ThoughtspotobjectsObjectIdShareusers(objectId!);
      } catch (error) {
        captureException(error, {
          level: 'error',
          extra: { message: 'Failed to fetch share users', objectId },
        });
        throw new Error('Failed to load users. Please try again.');
      }
    },
    enabled: !!objectId,
    staleTime: DEFAULT_QUERY_STALE_TIME,
    retry: CRITICAL_QUERY_RETRY_COUNT,
    retryDelay: (attemptIndex: number) => Math.min(BASE_RETRY_DELAY_MS * 2 ** attemptIndex, MAX_RETRY_DELAY_MS),
    ...options,
  });
}
